<script setup lang="ts">
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CreateCompanyForm from '@/components/CreateCompanyForm.vue'
import CreateCustomerForm from '@/components/CreateCustomerForm.vue'
import CreateTicketForm from '@/components/CreateTicketForm.vue'
import DialogCheck from '@/components/DialogCheck.vue'
import { useColorMode } from '@/composables/src/useColorMode'
import { Button } from '@/foundation/button'
import { Dialog, DialogContent, DialogHeader } from '@/foundation/dialog'
import { SidebarInset, SidebarProvider } from '@/foundation/sidebar'
import DefaultNav from '@/layouts/default/DefaultNav.vue'
import { useAccountStore } from '@/stores/account'
import { useQueuesStore } from '@/stores/queues'
import { useTicketStore } from '@/stores/tickets'
import { eventBus } from '@/utils/utils'
import Sidebar from './Sidebar.vue'

const route = useRoute()
const router = useRouter()
const queuesStore = useQueuesStore()
const ticketStore = useTicketStore()
const { showTicketNavigation } = storeToRefs(ticketStore)
const isDialogOpen = ref(false)
const { isClientUser, userRole } = useAccountStore()
const { mode } = useColorMode()

const isTicketDetailPage = computed(() => {
  return route.name === 'TicketDetails' && route.params.ticketId
})

const openDialog = () => {
  // For Projects page, emit event to open AddProductForm
  if (route.name === 'Projects') {
    eventBus.emit('open-add-project-dialog')

    return
  }
  isDialogOpen.value = true
}

const closeDialog = () => {
  isDialogOpen.value = false
}

const navigateToTicket = async (direction: 'next' | 'previous') => {
  const currentTicketId = route.params.ticketId as string

  try {
    const ticket =
      direction === 'next'
        ? await ticketStore.getNextTicket(currentTicketId)
        : await ticketStore.getPreviousTicket(currentTicketId)

    if (ticket) {
      router.push({ name: 'TicketDetails', params: { ticketId: ticket.id }, query: { ticket: ticket.ticketNumber } })
    }
  } catch (error) {
    console.error('Error on navigation:', error)
  }
}

const dynamicTitle = computed<string>(() => {
  const raw = route.meta.title

  if (typeof raw === 'function') {
    return raw(route, queuesStore.queues)
  }

  return (raw as string) || (route.name as string)
})

const formName = computed(() => {
  switch (route.name) {
    case 'Tickets':
    case 'QueueView':
      return CreateTicketForm
    case 'Companies':
      return CreateCompanyForm
    case 'Customers':
      return CreateCustomerForm
    case 'Projects':
      return null // Projects uses AddProductForm with its own v-if pattern
    default:
      return null
  }
})

onBeforeMount(async () => {
  await queuesStore.fetchQueues()
})

watch(
  () => route.params.ticketId,
  (ticketId) => {
    if (ticketId && typeof ticketId === 'string') {
      ticketStore.updateNavigationState(ticketId)
    }
  },
  { immediate: true },
)
</script>

<template>
  <SidebarProvider class="flex flex-1 flex-col md:flex-row">
    <Sidebar v-if="!isClientUser" />
    <SidebarInset>
      <DefaultNav v-if="route.meta.showBreadcrumb" />
      <div class="flex flex-1 flex-col gap-2 sm:gap-4 overflow-hidden">
        <div :class="['flex-1 px-0']">
          <div
            v-if="isTicketDetailPage && showTicketNavigation"
            class="flex items-center justify-between flex-wrap gap-2 px-4 py-5 pb-0"
          >
            <div class="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                class="flex items-center gap-1 bg-background-light border border-input shadow-sm"
                :disabled="!ticketStore.hasPreviousTicket"
                @click="navigateToTicket('previous')"
              >
                <ChevronLeft class="w-5 h-5" />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                class="flex items-center gap-1 bg-background-light border border-input shadow-sm"
                :disabled="!ticketStore.hasNextTicket"
                @click="navigateToTicket('next')"
              >
                <span
                  style="
                    font-family: var(--font-sans, 'Inter', 'Helvetica', 'Arial', sans-serif);
                    font-weight: 400;
                    font-style: normal;
                    font-size: 0.875rem;
                    line-height: 1.25rem;
                    letter-spacing: 0%;
                    vertical-align: middle;
                  "
                  >Next Ticket</span
                >
                <ChevronRight class="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div v-if="route.meta.showTitle" class="flex items-center justify-between flex-wrap gap-2 px-4 py-5">
            <h2 v-if="!isTicketDetailPage" class="text-xl sm:text-[1.375rem] font-semibold tracking-tight">
              {{ dynamicTitle }}
            </h2>
            <Button v-if="route.name === 'Tickets' || route.name === 'QueueView'" size="sm" @click="openDialog">
              <Plus class="w-4 h-4 mr-1" />
              <span class="mr-2">New Ticket</span>
            </Button>
            <Button
              v-if="route.name === 'Companies' && (userRole === 'admin' || userRole === 'manager')"
              size="sm"
              @click="openDialog"
            >
              <Plus class="w-4 h-4 mr-1" />
              <span class="mr-2">New Company</span>
            </Button>
            <Button
              v-if="route.name === 'Customers' && (userRole === 'admin' || userRole === 'manager')"
              size="sm"
              @click="openDialog"
            >
              <Plus class="w-4 h-4 mr-1" />
              <span class="mr-2">New Customer</span>
            </Button>
            <Button
              v-if="route.name === 'Projects' && (userRole === 'admin' || userRole === 'manager')"
              size="sm"
              @click="openDialog"
            >
              <Plus class="w-4 h-4 mr-1" />
              <span class="mr-2">New Project</span>
            </Button>
          </div>
          <div class="h-full flex-1 flex-col flex">
            <slot></slot>
          </div>
        </div>
      </div>
    </SidebarInset>
  </SidebarProvider>
  <!-- Ticket Creation Dialog -->
  <Dialog v-model:open="isDialogOpen">
    <DialogContent
      :is-closable="false"
      class="grid-rows-[auto_1fr_auto] !max-h-[90dvh] !max-w-[816px] p-0 !gap-0"
      :class="{
        'bg-background-light': mode === 'light',
        'bg-background-dark': mode === 'dark',
      }"
    >
      <!-- Dialog Header -->
      <DialogHeader
        class="w-full p-3 sm:p-4 px-4 sm:px-6 !pr-2 border-b flex flex-row justify-between rounded-t-lg"
        :class="{
          'bg-background-light': mode === 'light',
          'bg-background-dark': mode === 'dark',
        }"
      >
        <h2 v-if="route.name === 'Tickets'" class="text-lg sm:text-xl font-bold p-0">Create Ticket</h2>
        <h2 v-if="route.name === 'Companies'" class="text-lg sm:text-xl font-bold p-0">Create Company</h2>
        <h2 v-if="route.name === 'Customers'" class="text-lg sm:text-xl font-bold p-0">Create Customer</h2>
        <DialogCheck @dialog-should-close="closeDialog">
          <template #icon>
            <X class="w-5 h-5" />
          </template>
        </DialogCheck>
      </DialogHeader>

      <!-- Ticket Creation Form -->
      <Component
        :is="formName"
        class="w-full p-3 px-6 overflow-y-auto pt-6"
        :class="{
          'bg-background-light': mode === 'light',
          'bg-background-dark': mode === 'dark',
        }"
        @close-dialog="closeDialog"
        @dialog-should-close="closeDialog"
      />
    </DialogContent>
  </Dialog>
</template>
